import { initializeApp, getApps } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getAnalytics, isSupported, type Analytics } from "firebase/analytics";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

// Initialize Analytics only if supported AND user has consented
let analytics: Analytics | null = null;

// Check for debug mode via URL param or localStorage
const isDebugMode = () => {
  if (typeof window === "undefined") return false;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("debug_mode") === "true" || localStorage.getItem("analytics_debug") === "true";
};

// Check if user has consented to analytics (GDPR compliance)
const hasAnalyticsConsent = () => {
  if (typeof window === "undefined") return false;
  try {
    const consent = localStorage.getItem("cookie_consent");
    if (consent) {
      const parsed = JSON.parse(consent);
      return parsed.analytics === true;
    }
  } catch {
    // Ignore parse errors
  }
  return false;
};

const analyticsReady = isSupported().then((supported) => {
  // Only initialize analytics if supported AND user has consented
  if (supported && hasAnalyticsConsent()) {
    analytics = getAnalytics(app);
    // Log debug status
    if (isDebugMode()) {
      console.log("🔍 Firebase Analytics Debug Mode ENABLED");
      // Set debug mode for this session
      (window as Window & { gtag?: (...args: unknown[]) => void }).gtag?.("config", firebaseConfig.measurementId, {
        debug_mode: true,
      });
    }
    return analytics;
  }
  if (supported && !hasAnalyticsConsent()) {
    console.log("📊 Analytics disabled - awaiting user consent");
  }
  return null;
});

// Getter function for analytics - returns immediately if ready, otherwise waits
export const getAnalyticsInstance = () => analyticsReady;

export { app, auth, db, googleProvider, analytics };
